import React, { createContext, useContext, useState } from 'react';

// Manually-added appointments (the "+ Add appointment" flow). The mock fixtures
// are static; this localStorage-backed store holds appointments the user creates,
// which lib/data.js merges into the worklist + Appointments list. Mirrors the
// networkStore pattern (synchronous writes + a non-hook reader for lib/data).

const LS_KEY = 'due_health_appts';
const EMPTY = { appointments: [] };

function read() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...EMPTY, ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }
  return { ...EMPTY };
}

function write(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  return state;
}

// Non-hook reader for lib/data.js (which is not a React component).
export function addedAppointmentsFor(practiceId) {
  return read().appointments.filter((a) => practiceId === 'all' || !practiceId || a.practiceId === practiceId);
}

const ApptContext = createContext(null);

export function ApptProvider({ children }) {
  const [state, setState] = useState(read);
  const commit = (next) => { write(next); setState(next); return next; };

  // Add an appointment. record: { practiceId, patientId?, patient?, providerId,
  //   startTime, cpt, reason }. patientId references an existing patient; OR
  //   patient holds an inline new-patient object { firstName, lastName, dob,
  //   payerId, planId?, memberIdLast4?, isNewPatient }.
  const addAppointment = (record) => {
    const cur = read();
    const appt = {
      id: `apt_add_${cur.appointments.length + 1}_${Date.now()}`,
      patientId: null,
      patient: null,
      reason: '',
      source: 'manual',
      createdAt: new Date().toISOString(),
      ...record,
    };
    commit({ ...cur, appointments: [...cur.appointments, appt] });
    return appt;
  };

  const reset = () => commit({ ...EMPTY });

  const value = {
    appointments: state.appointments,
    addAppointment,
    reset,
    addedAppointments: (practiceId) => state.appointments.filter((a) => practiceId === 'all' || !practiceId || a.practiceId === practiceId),
  };
  return <ApptContext.Provider value={value}>{children}</ApptContext.Provider>;
}

export function useAppts() {
  const ctx = useContext(ApptContext);
  if (!ctx) throw new Error('useAppts must be inside ApptProvider');
  return ctx;
}
